// Wave-2 "planned" tabs — observability domains that are designed but gated on a data
// source we don't have access to yet (ClickHouse, Tempo, exporters, billing, RUM).
// Each renders a clear planned state + the intended panels via <ScaffoldTab slug=...>.
// When a domain's dependency lands, replace its route with the real tab + API module.

export interface ScaffoldSpec {
  phase: string;        // roadmap phase id, e.g. "P3"
  title: string;
  desc: string;
  dependency: string;   // what must be connected for this to light up
  panels: string[];     // the planned panels, shown as preview cards
}

export const SCAFFOLDS: Record<string, ScaffoldSpec> = {
  "platform-kpis": {
    phase: "P3", title: "Platform KPIs",
    desc: "Business & money observability — the platform's vital signs, not just servers.",
    dependency: "ClickHouse analytics warehouse (creds + schema from the API pod)",
    panels: ["Payment success rate", "Settlement success & latency", "Escrow state distribution",
             "GMV (SAR) trend", "KYC throughput & pass rate", "Webhook delivery success"],
  },
  "traces": {
    phase: "P5c", title: "Traces",
    desc: "Distributed tracing — the third pillar. Find the slow span behind a latency spike.",
    dependency: "Tempo datasource + OpenTelemetry instrumentation coverage",
    panels: ["Slowest endpoints (p95/p99)", "Trace search", "Service latency heatmap",
             "Exemplar links from metric spikes", "Error-trace explorer"],
  },
  "data-stores": {
    phase: "P6", title: "Data Stores",
    desc: "PostgreSQL, MongoDB, ClickHouse, Redis health in one place.",
    dependency: "DB exporters scraped by Prometheus (postgres/mongodb/redis exporters)",
    panels: ["Connections & pool saturation", "Slow queries", "Replication lag",
             "Cache hit rate", "Disk growth", "Backup freshness"],
  },
  "security": {
    phase: "P9", title: "Security & Compliance",
    desc: "Auth health, certs, secrets, and SAMA/KYC posture for a fintech.",
    dependency: "Keycloak metrics, cert-exporter, KYC service metrics",
    panels: ["Login success & token-failure rate", "Brute-force / anomaly signals", "TLS cert expiry",
             "Secret-rotation age", "KYC pipeline (Absher/Yakeen/Nafath)", "Hub audit trail"],
  },
  "cost": {
    phase: "P10", title: "Cost & Capacity",
    desc: "Cloud spend and efficiency by service — operational excellence includes the bill.",
    dependency: "Cloud billing export (AWS CUR / GCP BigQuery billing)",
    panels: ["Spend by service / namespace", "Cost per deploy & per transaction",
             "Rightsizing (requests vs usage)", "Capacity forecast", "Idle-resource flags"],
  },
  "ai-agents": {
    phase: "P11", title: "AI Agents",
    desc: "Observability for the waffy-agents platform + AutoSettle / Reconciliation agents.",
    dependency: "Agent run metrics + ClickHouse run logs",
    panels: ["Run volume & latency per agent", "LLM token cost", "Error / failure rate",
             "Shadow-vs-auto decision drift", "Guardrail-override rate"],
  },
  "data-pipelines": {
    phase: "P12", title: "Data Pipelines",
    desc: "Ingestion freshness and job health from source systems into ClickHouse.",
    dependency: "Airbyte API + ClickHouse ingestion metadata (Data Platform / WEA-26)",
    panels: ["Ingestion freshness & lag", "Job success / failure", "Schema-drift alerts",
             "Row-count anomalies", "Per-connector status"],
  },
};
